import React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import { alpha, useTheme } from '@mui/material/styles';

/** Defaults for full-width scrollable tab strips (scroll buttons, indicator, etc.) */
export const SCROLLABLE_TABS_DEFAULT_PROPS = {
  variant: 'scrollable',
  scrollButtons: 'auto',
  allowScrollButtonsMobile: true,
  indicatorColor: 'primary',
  textColor: 'primary',
  TabScrollButtonProps: {
    sx: {
      opacity: 0.9
    }
  }
};

/**
 * Shared `sx` for MUI Tabs with horizontal scroll, spaced tabs, hover tint, and no trailing margin on the last tab.
 *
 * @param {import('@mui/material/styles').Theme} theme
 * @param {{ tabPaddingY?: number }} [options] Optional theme-spacing `py` on each tab (theme spacing units).
 */
export function getScrollableTabsSx(theme, { tabPaddingY } = {}) {
  const tabVerticalPadding =
    typeof tabPaddingY === 'number' ? { py: tabPaddingY } : {};

  return {
    width: '100%',
    minHeight: 48,
    '& .MuiTabs-flexContainer': {
      minWidth: '100%',
      width: 'max-content'
    },
    '& .MuiTabScrollButton-root.Mui-disabled': {
      opacity: 0.38
    },
    '& .MuiTabScrollButton-root.MuiTabScrollButton-disabled': {
      opacity: 0.38
    },
    '& .MuiTab-root': {
      minHeight: 48,
      flex: '1 0 0%',
      minWidth: 'max-content',
      maxWidth: 'none',
      textTransform: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: theme.typography.fontFamily,
      px: { xs: 2, sm: 3 },
      mr: { xs: 0.5, sm: 1 },
      '&:last-of-type': {
        mr: 0
      },
      ...tabVerticalPadding,
      transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shortest,
        easing: theme.transitions.easing.easeInOut
      }),
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.06)
        },
        '&.Mui-selected:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1)
        }
      }
    },
    '& .Mui-selected': {
      fontWeight: 600
    }
  };
}

function sxToArray(sx) {
  if (!sx) return [];
  return Array.isArray(sx) ? sx : [sx];
}

/**
 * Opinionated scrollable MUI `Tabs`: flex layout, optional tab vertical padding, merged `sx`, rest props forwarded.
 */
export default function ScrollableTabs({
  value,
  onChange,
  children,
  sx,
  tabPaddingY,
  ...tabsProps
}) {
  const theme = useTheme();
  const { sx: tabsPropsSx, ...restTabsProps } = tabsProps;

  const mergedSx = [
    getScrollableTabsSx(theme, { tabPaddingY }),
    ...sxToArray(sx),
    ...sxToArray(tabsPropsSx)
  ];

  return (
    <Tabs
      {...SCROLLABLE_TABS_DEFAULT_PROPS}
      value={value}
      onChange={onChange}
      sx={mergedSx}
      {...restTabsProps}
    >
      {children}
    </Tabs>
  );
}

ScrollableTabs.propTypes = {
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  tabPaddingY: PropTypes.number
};
